/**
 * Pure helpers behind the Members tab's two sections.
 *
 * `GET /group/{id}/members` returns ONE list holding two kinds of edge: people
 * an admin added, and people who joined themselves (`Subscribed: true`). This
 * tab used to filter the second kind out and render only the first, so a
 * member who had also subscribed - which is one edge, not two, since the
 * subscribe write `$set`s the flag onto an existing edge - vanished from Group
 * Manager while staying visible everywhere else.
 *
 * Kept separate from the components so the rules can be tested without
 * mounting Ionic.
 */

/** The loosely-typed member row shape these helpers need. */
export interface MemberSectionRow {
  Subscribed?: boolean;
  [key: string]: unknown;
}

/**
 * Roles whose assignment converts a self-subscribed edge into a managed one.
 *
 * Admin and Editor only. Guest and None are informational edges that papi's
 * `ACCESS_ROLE_LABELS` does not count as access at all, so converting on one
 * would promote somebody out of the Subscribers section while giving them
 * LESS standing, and plain Member is the role a subscriber effectively
 * already holds.
 */
export const ROLES_THAT_CONVERT_A_SUBSCRIBER = ["Admin", "Editor"] as const;

/** Whether this row is somebody who joined themselves. */
export function isSubscriber(member: MemberSectionRow): boolean {
  return member?.Subscribed === true;
}

/**
 * Partition member rows into the two sections the tab renders.
 *
 * Every row lands in exactly one section - the pair is the whole list, which
 * is the property that stops anyone being dropped again.
 */
export function splitBySubscription<T extends MemberSectionRow>(
  rows: T[] | null | undefined
): { members: T[]; subscribers: T[] } {
  const members: T[] = [];
  const subscribers: T[] = [];
  for (const row of rows ?? []) {
    (isSubscriber(row) ? subscribers : members).push(row);
  }
  return { members, subscribers };
}

/**
 * The `Subscribed` value to send with a member save, or undefined to send none.
 *
 * Returns `false` only when a subscriber is being given a real role, so an
 * ordinary role change or a notification toggle can never convert somebody as
 * a side effect. papi treats an absent field as "leave the stored flag alone",
 * and only an explicit `false` clears it.
 */
export function subscribedFlagForSave(
  member: MemberSectionRow,
  role: string
): boolean | undefined {
  if (!isSubscriber(member)) return undefined;
  return (ROLES_THAT_CONVERT_A_SUBSCRIBER as readonly string[]).includes(role)
    ? false
    : undefined;
}
